import { useCallback, useMemo } from 'react';
import { Show } from '../types/Show';
import { useScheduleQuery } from './queries/useScheduleQuery';
import { useLiveShowContext } from '../contexts/LiveShowContext';
import { TimeZoneUtils } from '../utils/timeZoneUtils';

interface ScheduleState {
  // Schedule-derived data (current and upcoming shows only)
  allScheduledShows: Show[];
  currentLiveShow: Show | null;
  upcomingToday: Show[];
  upcomingTomorrow: Show[];
  
  // Loading and error states
  loading: boolean;
  error: string | null;
  
  // Refresh function (schedule only)
  refreshSchedule: () => void;
  
  // Debug info
  timezoneInfo: {
    londonTime: string;
    localTime: string;
    isDST: boolean;
    offset: string;
  };
}

export const useScheduleState = (): ScheduleState => {
  // Fetch schedule data (current and upcoming shows only)
  const scheduleQuery = useScheduleQuery();
  const { refetch: refetchSchedule } = scheduleQuery;
  
  // Get current live show from LiveShowContext (authoritative source)
  const { currentLiveShow } = useLiveShowContext();
  
  // Derive today's upcoming shows from schedule, filtering out currently live show
  const upcomingToday = useMemo(() => {
    if (!scheduleQuery.data) return [];
    
    const allUpcomingShows = TimeZoneUtils.getTodaysUpcomingShows(scheduleQuery.data);
    
    // Filter out the currently live show from upcoming shows
    if (!currentLiveShow) return allUpcomingShows;
    
    return allUpcomingShows.filter(show => {
      // Remove show if it matches the currently live show
      return !(
        show.show === currentLiveShow.show &&
        show.date_start_time === currentLiveShow.date_start_time
      );
    });
  }, [scheduleQuery.data, currentLiveShow]);

  const upcomingTomorrow = useMemo(() => {
    if (!scheduleQuery.data) return [];

    const now = TimeZoneUtils.getCurrentLondonTime();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    const dayAfterTomorrow = new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000);

    return scheduleQuery.data
      .filter((show) => {
        const start = TimeZoneUtils.parseShowDateTime(show.date_start_time);
        return start >= tomorrow && start < dayAfterTomorrow;
      })
      .sort((a, b) => {
        const timeA = TimeZoneUtils.parseShowDateTime(a.date_start_time).getTime();
        const timeB = TimeZoneUtils.parseShowDateTime(b.date_start_time).getTime();
        return timeA - timeB;
      });
  }, [scheduleQuery.data]);
  
  // Get timezone info for debugging
  const timezoneInfo = useMemo(() => {
    return TimeZoneUtils.getTimezoneInfo();
  }, []);
  
  // Refresh function (schedule only)
  const refreshSchedule = useCallback(() => {
    refetchSchedule();
  }, [refetchSchedule]);
  
  return {
    // Schedule-derived data only
    allScheduledShows: scheduleQuery.data || [],
    currentLiveShow,
    upcomingToday,
    upcomingTomorrow,
    
    // Loading and error states (schedule only)
    loading: scheduleQuery.isLoading,
    error: scheduleQuery.error?.message || null,
    
    // Refresh function (schedule only)
    refreshSchedule,
    
    // Debug info
    timezoneInfo,
  };
};
